import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { AlertCircle, CheckCircle2 } from "lucide-react";

import { cn } from "./lib/utils";

const inputVariants = cva(
  "flex w-full rounded-lg border-2 bg-transparent shadow-sm transition-all file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      state: {
        default: "border-border focus-visible:border-ring focus-visible:ring-4 focus-visible:ring-ring/10",
        error: "border-destructive/30 focus-visible:border-destructive focus-visible:ring-4 focus-visible:ring-destructive/10",
        success: "border-success/30 focus-visible:border-success focus-visible:ring-4 focus-visible:ring-success/10",
        warning: "border-warning/30 focus-visible:border-warning focus-visible:ring-4 focus-visible:ring-warning/10",
      },
      /**
       * A step on the @smallbiz/design-tokens density scale (DensityMode). `comfortable`
       * reproduces the original sizing (px-4 py-2.5, text-base → md:text-sm) — existing
       * consumers are untouched. `compact` (opt-in, #1134) is the dense, keyboard-first step.
       * Hardcoded utilities for now; #1141 folds this into the token-driven, rescaled scale
       * (and adds `spacious`).
       */
      density: {
        comfortable: "px-4 py-2.5 text-base md:text-sm",
        compact: "px-2 py-1 text-sm",
      },
    },
    defaultVariants: {
      state: "default",
      density: "comfortable",
    },
  },
);

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {
  error?: string;
  success?: string;
  helperText?: string;
  label?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, state, density, type, error, success, helperText, label, ...props }, ref) => {
    // Determine the state based on error/success props
    const computedState = error ? "error" : success ? "success" : state || "default";
    
    // Determine the message to display
    const message = error || success || helperText;
    
    // ID for accessibility
    const inputId = props.id || React.useId();
    const messageId = message ? `${inputId}-message` : undefined;

    // Wrapper for label + input + helper text
    if (label || message) {
      return (
        <div className="w-full">
          {label && (
            <label htmlFor={inputId} className="block text-sm font-medium mb-2 text-foreground">
              {label}
            </label>
          )}
          <div className="relative">
            <input
              id={inputId}
              type={type}
              className={cn(
                inputVariants({ state: computedState, density }),
                (error || success) && "pr-10", // Add right padding for icon
                className
              )}
              ref={ref}
              aria-invalid={error ? "true" : "false"}
              aria-describedby={messageId}
              {...props}
            />
            {error && (
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <AlertCircle className="h-5 w-5 text-destructive" aria-hidden="true" />
              </div>
            )}
            {success && (
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <CheckCircle2 className="h-5 w-5 text-success" aria-hidden="true" />
              </div>
            )}
          </div>
          {message && (
            <p
              id={messageId}
              className={cn(
                "mt-1.5 text-xs flex items-center gap-1",
                error && "text-destructive",
                success && "text-success",
                !error && !success && "text-muted-foreground"
              )}
            >
              {error && <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
              {success && <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
              <span>{message}</span>
            </p>
          )}
        </div>
      );
    }

    // Simple input without wrapper (backward compatibility)
    return (
      <input
        type={type}
        className={cn(inputVariants({ state: computedState, density }), className)}
        ref={ref}
        aria-invalid={error ? "true" : "false"}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

export { Input, inputVariants };
